import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Gift, Trophy } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ChipRow, Empty, ErrorView, Loading } from "@/components/kit";
import { C } from "@/lib/format";

const PODIUM = [
  { idx: 1, color: C.silver, h: 90, key: "second" },
  { idx: 0, color: C.brand, h: 120, key: "first" },
  { idx: 2, color: C.bronze, h: 70, key: "third" },
];

function Prizes({ data }) {
  return (
    <div data-testid="ranking-prizes" className="flex flex-col gap-2 rounded-[20px] border border-[#D4AF37] bg-[#292415] p-4">
      <h2 className="t-h3 !text-[#D4AF37]">Récompenses de fin d'année {data.year}</h2>
      {["first", "second", "third"].map((k, i) => (
        <div key={k} className="flex items-center gap-2">
          <Gift className="h-4 w-4" style={{ color: [C.brand, C.silver, C.bronze][i] }} aria-hidden />
          <span className="text-sm text-white">
            {i + 1}
            {i === 0 ? "er" : "e"} : {data.prizes[k]}
          </span>
        </div>
      ))}
    </div>
  );
}

function Podium({ rows }) {
  return (
    <div className="mt-3 flex items-end gap-2">
      {PODIUM.map((p) => {
        const r = rows[p.idx];
        return (
          <div key={p.key} data-testid={`podium-${p.idx + 1}`} className="flex flex-1 flex-col items-center gap-1">
            {r ? (
              <>
                <span className="t-h3 w-full truncate text-center">{r.name}</span>
                <span className="t-caption">{r.points} pts</span>
              </>
            ) : (
              <span className="t-caption">—</span>
            )}
            <div className="flex w-full items-center justify-center rounded-t-xl border bg-[#1E1E1E]" style={{ height: p.h, borderColor: p.color }}>
              <span className="t-display" style={{ color: p.color }}>{p.idx + 1}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function RankingView({ testID }) {
  const { user } = useAuth();
  const [period, setPeriod] = useState("year");
  const q = useQuery({ queryKey: ["ranking", period], queryFn: () => api(`/ranking?period=${period}`) });
  const rows = q.data?.rows ?? [];
  return (
    <div data-testid={testID} className="flex flex-col">
      <div className="border-b border-[#222222] px-4 md:px-8">
        <ChipRow
          options={[
            { key: "year", label: `Année ${new Date().getFullYear()}` },
            { key: "month", label: "Meilleurs du mois" },
          ]}
          value={period}
          onChange={setPeriod}
          testIDPrefix="ranking-period"
        />
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.isError || !q.data ? (
        <ErrorView message="Erreur de chargement du classement." onRetry={() => q.refetch()} />
      ) : (
        <div className="mx-auto grid w-full max-w-6xl gap-6 p-4 pb-8 md:p-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <div className="flex flex-col gap-4">
            {period === "year" && <Prizes data={q.data} />}
            {rows.length === 0 ? (
              <Empty icon={Trophy} title="Classement en attente" text="Le classement se remplit automatiquement dès les premières recommandations." />
            ) : (
              <Podium rows={rows} />
            )}
            {q.data.me && user && (
              <div data-testid="ranking-my-position" className="flex items-center gap-3 rounded-[20px] border border-[#D4AF37] p-4">
                <span className="t-h2 !text-[#D4AF37]">{q.data.me.rank}e</span>
                <span className="flex-1 text-sm text-white">
                  Votre position · {q.data.me.points} pts · {q.data.me.signed} contrat(s)
                </span>
              </div>
            )}
            <p className="t-caption">Points : 10 par recommandation, 100 par contrat signé.</p>
          </div>
          <ol className="flex flex-col gap-2" data-testid="ranking-list">
            {rows.slice(3).map((item) => (
              <li
                key={item.agent_id}
                data-testid={`ranking-row-${item.rank}`}
                className={`flex items-center gap-3 rounded-xl p-3 ${item.agent_id === user?.id ? "bg-[#292415]" : "bg-[#1E1E1E]"}`}
              >
                <span className="t-h3 w-8 tabular-nums">{item.rank}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white">{item.name}</p>
                  <p className="t-caption truncate">
                    {item.agency} · {item.city}
                  </p>
                </div>
                <span className="t-h3 !text-[#D4AF37]">{item.points}</span>
              </li>
            ))}
            {rows.length > 0 && rows.length <= 3 && <li className="t-caption hidden lg:block">Les agents suivants apparaîtront ici.</li>}
          </ol>
        </div>
      )}
    </div>
  );
}
